// Token browser + pile search + dice, extracted from GameBoard.jsx.
// Builds the catalog/custom token entries the StatusTokenDrawer and
// token pile show, drives the PileSearchDialog (including the
// card-text search_deck effect with its filter gate and pick
// resolution), spawns cards/tokens from piles and the drawer, and
// owns the dice spawn/roll/value/delete actions.

import { peekCosmeticsCached } from '../../../utils/arena/cosmeticsCache';
import { buildTokenImageUrl } from '../../../utils/arena/customTokenUrl';
import { nearestCellAtWorld, siteSnapPoint } from '../../../utils/game/engine/core';
import { getGameGrid } from '../../../utils/game/spawnConfig';
import { FULL_SIZE_SPAWN_IDS } from '../../../utils/game/engine/automation/ops/spawn';
import { spawnTokenIntent } from '../../../utils/game/net/manualIntents';
import { deleteDiceIntent, rollDiceIntent, setDiceFaceIntent, spawnDiceIntent } from '../../../utils/game/net/diceIntents';
import { getCellNumber } from '../../../utils/game/grid';
import { playSound } from '../../../utils/game/sounds';
import { toast } from 'sonner';
import { cardLogLabel } from './logbookChat';
import { k, t } from '../../../localization/t';
import { msg } from '../../../utils/game/logMessage';

// Dice are server MatchState (authDice folds them): every gesture here is an
// intent, and the mesh appears from the diff. Fresh dice with no drop point
// line up in a row at the table's middle, a die's width apart.
const SPAWN_PITCH = 5;
const SPAWN_ROW = 6;

export function spawnDice(host, dieType, point = null) {
  const { x, z } = point ?? rowSlot(host.state.gameState.dice.length);
  host._sendMatchIntent(spawnDiceIntent({ dieType, seat: host._localSeat(), x, z }));
  playSound('cardPlace');
}

/** The nth slot of the centred fallback row. */
function rowSlot(n) {
  return {
    x: ((n % SPAWN_ROW) - (SPAWN_ROW - 1) / 2) * SPAWN_PITCH,
    z: Math.floor(n / SPAWN_ROW) * SPAWN_PITCH,
  };
}

export function rollDice(host, diceInstance) {
  host._sendMatchIntent(rollDiceIntent(diceInstance.id));
}

export function deleteDice(host, diceInstance) {
  host._sendMatchIntent(deleteDiceIntent(diceInstance.id));
  host.setState({ contextMenu: null });
}

export function setDiceValue(host, diceInstance, value) {
  host._sendMatchIntent(setDiceFaceIntent(diceInstance.id, value));
  host.setState({ contextMenu: null });
}

// Catalog-derived token entries: every zero-cost Minion / Artifact
// (Sorcery's generic tokens. Skeleton, Foot Soldier, Lance, etc.)
// plus the fixed full-size spawnables. Two groups belong here
// regardless of cost or type:
//   1. Full-sized spawnables (Rubble, Bruin, Tawny). Spawned by
//      specific card effects, and rendered at full card size
//      (`isToken: false`) rather than the small token scale.
//   2. Gothic "from Collection" minions and Grounds. In Sealed/Draft
//      players don't bring a Collection, so effects like Molten Mar
//      (play Hellhounds from your Collection) spawn token versions
//      from this list instead.
//
// Memoised on the `sorceryCards` prop identity: this walks the entire
// card catalog, and the left-edge token drawer reads it on every
// GameBoard render, so recomputing each time would be wasteful. The
// B-key Token Browser shares the same entries via `_allTokenEntries`.
export function buildCatalogTokenEntries(host) {
  const sorceryCards = host.props.sorceryCards || [];
  if (host._catalogTokenEntriesSrc === sorceryCards && host._catalogTokenEntries) {
    return host._catalogTokenEntries;
  }
  const tokens = sorceryCards
    .filter((c) => FULL_SIZE_SPAWN_IDS.has(c.unique_id) || (c.cost === '' && (c.type === 'Minion' || c.type === 'Artifact')))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const entries = tokens.map((card) => {
    // Always pick the Standard printing. Tokens must never show as foils.
    const printing = card.printings?.find((p) => p.foiling === 'S') || card.printings?.[0] || {};
    const fullSize = FULL_SIZE_SPAWN_IDS.has(card.unique_id);
    return {
      id: `token-${card.unique_id}`,
      cardId: card.unique_id,
      name: card.name,
      imageUrl: printing.image_url,
      printingId: printing.unique_id,
      foiling: 'S',
      type: card.type,
      isSite: card.type === 'Site',
      // Carry the catalog subtypes so a spawned minion token (Skeleton →
      // Undead, Foot Soldier → Mortal) is still recognised by subtype
      // filters (Persecutor's "Evil", count effects). Without this the
      // clone lands with no subTypes and matches nothing.
      subTypes: Array.isArray(card.traits) ? [...card.traits] : [],
      // Full-size spawnables render as ordinary cards; everything else
      // is a small token. Drives both the mesh scale on spawn and the
      // drawer/browser drag-and-drop clone.
      isToken: !fullSize,
    };
  });
  host._catalogTokenEntriesSrc = sorceryCards;
  host._catalogTokenEntries = entries;
  return entries;
}

// Cosmetic custom-token entries. Two sources:
//
//   1. Player-owned paid cosmetic tokens. `kind: 'token'` packs from
//      the cosmetics catalog where the player owns the entitlement.
//      Unowned paid tokens never appear (the store is the only
//      discovery path).
//   2. Universal tokens. The catalog's free-for-everyone token list
//      (status effects: burned, disabled, stealth, ward, etc.). No
//      ownership check.
//
// Both sources share the same on-disk format (versioned WebPs on
// Blob, fetched via /sorcery-images/<slug>.webp?v=N) and the same
// pile-entry shape, so they intersperse alphabetically with no
// visual distinction beyond their art.
//
// Empty array if the cache hasn't populated yet; opening the Token
// Browser / drawer a second time gets the full list.
export function buildCustomTokenEntries(host) {
  const cache = peekCosmeticsCached();
  if (!cache) return [];
  const owned = new Set(cache.owned || []);
  const ownedPaidTokens = (cache.packs || []).filter((p) => p.kind === 'token' && owned.has(p.cosmeticId));
  const universalTokens = Array.isArray(cache.universalTokens) ? cache.universalTokens : [];
  const allTokens = [...ownedPaidTokens, ...universalTokens];
  // Universal / cosmetic tokens carry only art + a display name, so they
  // default to the inert `Token` type. But several are skins of real
  // minion tokens (Skeleton, Foot Soldier). Match those back to the
  // catalog by name so they spawn as proper minions with their subtypes
  // (Undead, Mortal) and stay visible to subtype filters. Status markers
  // (Stealth, Ward, …) have no catalog row and stay inert Tokens.
  const catalogByName = new Map(
    (host.props.sorceryCards || [])
      .filter((c) => c.type === 'Minion' || c.type === 'Artifact')
      .map((c) => [(c.name || '').toLowerCase(), c]),
  );
  return allTokens
    .sort((a, b) => (a.tokenName || a.name || '').localeCompare(b.tokenName || b.name || ''))
    .map((pack) => {
      const catalogCard = catalogByName.get((pack.tokenName || pack.name || '').toLowerCase());
      return {
        // Universal tokens have no cosmeticId (no ownership row).
        // Fall back to the slug so the pile entry id stays unique.
        id: `token-${pack.cosmeticId || pack.tokenSlug}`,
        cardId: pack.cosmeticId || pack.tokenSlug,
        // Reuses the Sorcery image fetch path. Server proxies to Blob
        // and caches on first hit. The `?v=<tokenVersion>` query is a
        // cache-busting tag: when token art is re-uploaded under the
        // same slug, bumping `tokenVersion` in the server catalog
        // changes the URL so Electron's HTTP cache and the server's
        // persistent disk cache both miss and refetch the new bytes.
        // Falls back to the bare URL if the server hasn't sent a
        // version yet (older deployments).
        imageUrl: buildTokenImageUrl(pack),
        printingId: pack.cosmeticId || pack.tokenSlug,
        foiling: 'S',
        // A catalog match means this token skins a real minion/artifact.
        // adopt its type + subtypes so automations treat it as such. No
        // match (pure status markers) keeps the inert `Token` type.
        type: catalogCard?.type || 'Token',
        subTypes: Array.isArray(catalogCard?.traits) ? [...catalogCard.traits] : [],
        isSite: false,
        isToken: true,
        // Carried so the pile-search dialog can show the token's
        // display name (it falls back to `name`).
        name: pack.tokenName || pack.name,
      };
    });
}

export function openPileSearch(host, pile) {
  if (host.props.isSpectating) return;
  host.setState({ searchPile: pile, searchQuery: '', contextMenu: null });
}

// v1.86. Kick off a SEARCH_DECK effect. Resolves the target pile
// (spellbook / atlas / cemetery) for the controller, opens the
// pile-view dialog, and stashes the active filter on
// pendingSearchEffect so click resolution gates on it. Per codex
// Search: a private zone (spellbook / atlas) lets the player fail
// to find; we offer the same Cancel behavior via the dialog's
// existing close button.
export function startSearchEffect(host, sourceCard, controller, args, sourceName) {
  if (host.props.isSpectating) return;
  const pileName = (args.pile || 'spellbook')[0].toUpperCase() + (args.pile || 'spellbook').slice(1);
  const playerNum = controller === 'p2' ? 2 : 1;
  const pile = host.findPileByNameForPlayer(pileName, playerNum);
  if (!pile) {
    host.logAction('{source}: {pile} not found. Effect fizzles', { source: sourceName || msg('Search'), pile: msg(pileName) });
    return;
  }
  if (pile.cards.length === 0) {
    host.logAction('{source}: {pile} is empty. Effect fizzles', { source: sourceName || msg('Search'), pile: msg(pileName) });
    return;
  }
  host.setState({
    searchPile: pile,
    searchQuery: '',
    pendingSearchEffect: {
      sourceCard,
      controller,
      pileId: pile.id,
      filter: args.filter || {},
      shuffleAfter: args.shuffle_after !== false,
      sourceName: sourceName || k('Search'),
    },
  });
}

// v1.86. Does a candidate pile card satisfy the SEARCH_DECK filter?
// Filters: type (e.g. 'Artifact'), subtype (string or array, case-
// insensitive), cost_at_most (numeric cap), rarity (e.g. 'Ordinary',
// 'Exceptional'). Multiple filters combine with AND.
export function searchEffectMatches(host, cardInstance, filter) {
  if (!filter || Object.keys(filter).length === 0) return true;
  const full = host._getFullCard(cardInstance.cardId);
  if (!full) return false;
  if (filter.type && full.type !== filter.type) return false;
  if (filter.subtype) {
    const wanted = (Array.isArray(filter.subtype) ? filter.subtype : [filter.subtype])
      .map((s) => String(s).toLowerCase());
    const subs = (full.subTypes || []).map((s) => String(s).toLowerCase());
    if (!wanted.some((w) => subs.includes(w))) return false;
  }
  if (typeof filter.cost_at_most === 'number') {
    if ((full.cost ?? Infinity) > filter.cost_at_most) return false;
  }
  if (filter.rarity && full.rarity !== filter.rarity) return false;
  return true;
}

// v1.86. Handle a click in the search dialog while a SEARCH_DECK
// effect is pending. Gates on the filter (toast on mismatch), takes
// the picked card to hand, shuffles the searched pile per codex,
// and clears the pending state + dialog.
export function resolveSearchPick(host, cardInstance) {
  const eff = host.state.pendingSearchEffect;
  if (!eff) return;
  if (!host._searchEffectMatches(cardInstance, eff.filter)) {
    toast.error(t('{source}: that card does not match the search.', { source: eff.sourceName }));

    return;
  }
  const pile = host.state.gameState.piles.find((p) => p.id === eff.pileId);
  if (!pile) return;
  host.logAction('{source}: found {card} in {pile}', { source: eff.sourceName, card: cardLogLabel(cardInstance), pile: msg(pile.name) });
  // silent: the named summary above already reports the take.
  host.takeCardFromPile(pile, cardInstance, { silent: true });
  if (eff.shuffleAfter) host.shufflePileAction(pile);
  host.setState({ pendingSearchEffect: null, searchPile: null, searchQuery: '' });
}

export function handlePileSearchClose(host) {
  // v1.86. Closing the dialog while a SEARCH_DECK is pending counts
  // as "fail to find" per codex (private-zone search may fizzle).
  // No shuffle on cancel. Codex only mandates the shuffle when the
  // search actually completes.
  if (host.state.pendingSearchEffect) {
    const eff = host.state.pendingSearchEffect;
    host.logAction('{source}: search ended (nothing taken)', { source: eff.sourceName });
  }
  if (host.state.pendingMaskPick) {
    // Mana is only spent on confirm. Closing without picking is a
    // free abort, so the log just notes the cancellation.
    host.logAction('Imposter: mask change cancelled');
  }
  if (host.state.pendingArchimagoBanish) {
    // Any picks that already landed in Banished stay there. The
    // partial cost was already committed on each pick. Sandbox
    // expectation: no automatic refund mid-flow.
    const n = host.state.pendingArchimagoBanish.picked.length;
    host.logAction('Archimago: cast a copy cancelled ({n}/3 banished)', { n });
  }
  if (host.state.pendingArchimagoCopy) {
    host.logAction('Archimago: copy cancelled (the three banishes stand)');
  }
  if (host.state.pendingDeathspeakerPick) {
    // Nothing was paid or banished yet. Closing is a free abort and
    // the once-per-turn budget stays unburned.
    host.logAction('Deathspeaker: cast a copy cancelled');
  }
  if (host.state.pendingPileSelect) {
    // Multi-select sessions commit nothing before Accept, so closing
    // at any point is a free abort. cancelPileSelect also re-checks
    // the deferred turn pass an end-of-turn pick may be holding open.
    host._cancelPileSelect();
  }
  const dragonPick = host.state.pendingDragonPick;
  if (dragonPick) {
    host.logAction('Dragonlord: set-aside cancelled');
  }
  if (host.state.pendingNameSpellPick) {
    // Mandatory server prompt (Feast for Crows): closing sends NO decline.
    // the server keeps the prompt open and the next mirror fold re-opens
    // the picker. Only the local dialog state clears here.
    host.logAction('Name a spell: a spell must still be named');
  }
  host.setState({
    searchPile: null,
    searchQuery: '',
    pendingSearchEffect: null,
    pendingMaskPick: null,
    pendingArchimagoBanish: null,
    pendingArchimagoCopy: null,
    pendingDeathspeakerPick: null,
    pendingDragonPick: null,
    pendingNameSpellPick: null,
  });
  // Closing the pre-game set-aside picker DECLINES the server prompt (it is
  // optional). The set-aside is forfeited and the server's preGame advance
  // unblocks for this seat.
  if (dragonPick) host._declineDragonlordSetAside?.(dragonPick);
}

export function handlePileSearchQueryChange(host, value) {
  host.setState({ searchQuery: value });
}

// Remove `card` from `sourcePile` and place it on the board. Caller
// passes the source pile explicitly so this works after tab switches
// where `state.searchPile` may have moved on. In casual sandbox the
// pile's `rotated` flag is the ownership ground truth. Sync the
// card to it so addCardToTable lands on the correct side.
export function spawnCardFromPile(host, sourcePile, card) {
  if (!sourcePile) return;

  if (sourcePile.infinite) {
    const isLocalP2 = host.props.isCasualSandbox
      ? host.state.activePlayerNum === 2
      : !host.state.isHost;
    // Auth: the server mints the token so it is real MatchState. Both seats
    // (and spectators) render it via rebuildBoardFromMirror. Sites and
    // non-catalog entries keep the legacy local clone (cosmetic path).
    const fullTokenRow = !card.isSite && card.cardId ? host._getFullCard(card.cardId) : null;
    if (host._authZonesEnabled?.() && fullTokenRow) {
      const cell = host._cellAtWorld(0, 0);
      if (cell) {
        host._sendMatchIntent(spawnTokenIntent({
          cardId: card.cardId, seat: isLocalP2 ? 'p2' : 'p1', col: cell.col, row: cell.row,
        }));
        host.logAction('Spawned {card}', { card: fullTokenRow.name || msg('token') });
        return;
      }
    }
    const clone = { ...card, id: `${card.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, x: 0, z: 0, rotated: isLocalP2 };
    host.addCardToTable(clone);
    return;
  }

  // Match by id (see takeCardFromPile for the stale-reference rationale).
  // If the card is no longer in the pile, abort. Calling addCardToTable
  // with no removal would leave the card both in the pile AND on the
  // table, and the addCardToTable guard would then refuse the second
  // spawn anyway. Bailing here keeps the broadcasts honest.
  const idx = sourcePile.cards.findIndex((c) => c.id === card.id);
  if (idx === -1) return;
  const removed = sourcePile.cards[idx];
  sourcePile.cards.splice(idx, 1);
  if (host.props.isCasualSandbox) {
    removed.rotated = !!sourcePile.rotated;
  }
  removed.x = 0;
  removed.z = 0;
  host.addCardToTable(removed);
  host.setState((state) => ({
    searchPile: sourcePile.cards.length === 0 ? null : state.searchPile,
  }));
  host.updatePileMeshes();
}

// Drop from the left-edge token drawer onto the table. The drawer
// carries a 2D ghost during the drag; on mouseup it hands off the
// pile entry (the same shape `_allTokenEntries` feeds the B-key Token
// Browser) + cursor screen coords. We raycast to the table plane for
// the world position, then clone the entry with a fresh id at that
// point and route it through addCardToTable. So the entry's own
// `isToken` flag decides full-size vs small and a catalog minion
// token lands as a real minion. Sites are the exception: they can't
// sit at a freeform point, so they snap to the cell under the drop.
export function spawnTokenFromDrawer(host, token, { clientX, clientY }) {
  if (!token || !host.scene) return;
  const point = host.scene.raycastTablePoint({ clientX, clientY });
  if (!point) return;
  const isLocalP2 = host.props.isCasualSandbox
    ? host.state.activePlayerNum === 2
    : !host.state.isHost;

  // Sites (the gothic Grounds) anchor to a grid cell like every other
  // site spawn. Resolve the cell under the drop and route through the
  // canonical site recipe (`_spawnSiteAtCell`, which uses siteSnapPoint
  // orientation). A drop off the grid is a no-op rather than a
  // misplaced site.
  if (token.isSite) {
    const cell = host._cellAtWorld(point.x, point.z);
    const cardRow = host._getFullCard(token.cardId);
    if (!cell || !cardRow) return;
    host._spawnSiteAtCell(cardRow, cell, {
      rotated: isLocalP2,
      logLine: `Spawned ${cardRow.name || token.name || 'site'} at [${getCellNumber(cell)}]`,
    });
    playSound('cardPlace');
    return;
  }

  // Auth: catalog tokens spawn server-minted at the cell under the drop, so
  // the token is real MatchState (renders from the diff on every client).
  // An off-grid drop snaps to the nearest cell: the server refuses a spawn
  // with no cell, and the old local-mint fallback produced a token the
  // server had never heard of. It had no cell, ranked under every site
  // stack, and "fell through" any site it was later dragged onto.
  // Non-catalog cosmetic tokens keep the local path.
  const fullDrawerRow = token.cardId ? host._getFullCard(token.cardId) : null;
  if (host._authZonesEnabled?.() && fullDrawerRow) {
    const exact = host._cellAtWorld(point.x, point.z);
    const cell = exact || nearestCellAtWorld(getGameGrid(host.state.spawnConfig), point.x, point.z);
    if (cell) {
      // The raw drop point rides the intent (free card placement): the server
      // persists it as the token's pos so a snap-off client renders the token
      // exactly where it was dropped (same as the Token Browser path). A
      // snapped drop uses the cell centre instead.
      host._sendMatchIntent(spawnTokenIntent({
        cardId: token.cardId, seat: isLocalP2 ? 'p2' : 'p1', col: cell.col, row: cell.row,
        x: exact ? point.x : cell.x, z: exact ? point.z : cell.z,
      }));
      host.logAction('Spawned {card} at [{cell}]', { card: fullDrawerRow.name || msg('Token'), cell: getCellNumber(cell) });
      playSound('cardPlace');
      return;
    }
  }

  const instance = {
    ...token,
    id: `${token.id || token.cardId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: token.name || host._getFullCard(token.cardId)?.name || 'Token',
    x: point.x,
    y: 0,
    z: point.z,
    rotated: isLocalP2,
  };
  // logVerb: k('Spawned') so the logbook reads "Spawned Stealth"
  // instead of "Played Stealth" on both clients. Tokens aren't
  // "played" in the card-game sense, they're conjured onto the
  // battlefield.
  host.addCardToTable(instance, true, { logVerb: k('Spawned') });
  // Match the audio cue of dropping a card from hand to table. A
  // token landing on the field should sound like a real card play.
  // addCardToTable on its own doesn't fire a place sound (that's
  // normally bound to the card:release event at the end of a drag).
  // The opponent picks this sound up via the card:place receive
  // handler.
  playSound('cardPlace');
}

export function handlePileSearchCardContextMenu(host, cardInstance, event) {
  const { searchPile } = host.state;
  if (!searchPile) return;
  host.setState({
    contextMenu: {
      type: 'pileCard',
      x: event.clientX,
      y: event.clientY,
      cardInstance,
      pile: searchPile,
    },
  });
}

// Move a card from one pile to its owner's Banished zone. The source
// pile's rotated flag is authoritative for ownership in casual
// sandbox so banishing from "Cemetery P1" always lands in "Banished
// P1." The menu hides this action for infinite (token) piles and for
// Banished sources; the guards here are belt-and-suspenders.
export function banishCardFromPile(host, sourcePile, cardInstance, opts = {}) {
  if (!sourcePile || !cardInstance || sourcePile.infinite) {
    host.closeContextMenu();
    return;
  }

  // Match by id (see takeCardFromPile for the stale-reference rationale).
  // Locate the live pile entry FIRST. If the card isn't actually in
  // the source pile any more, abort instead of duplicating it into the
  // destination on a stale reference.
  const idx = sourcePile.cards.findIndex((c) => c.id === cardInstance.id);
  if (idx === -1) {
    host.closeContextMenu();
    return;
  }
  const removed = sourcePile.cards[idx];

  if (host.props.isCasualSandbox) {
    removed.rotated = !!sourcePile.rotated;
  }

  const banishedPile = host.findOrCreateBanished(removed.rotated);
  if (!banishedPile || banishedPile.id === sourcePile.id) {
    host.closeContextMenu();
    return;
  }

  // Idempotency on the destination pile too: if a previous fire already
  // pushed this card into Banished (e.g. via a racy double-click), don't
  // append it a second time.
  if (banishedPile.cards.some((c) => c.id === removed.id)) {
    sourcePile.cards.splice(idx, 1);
    host.updatePileMeshes();
    host.closeContextMenu();
    return;
  }

  sourcePile.cards.splice(idx, 1);
  banishedPile.cards.push(removed);
  // Authoritative move (pile -> banished). Without it the server's zone still
  // says cemetery/spellbook and the next diff's pile reconciler drops the card
  // from Banished and re-renders it where the mirror has it (qa#170).
  if (host._authZonesEnabled?.()) host._emitMoveToZone(removed.id, 'banished');

  if (!opts.silent) {
    host.logAction('Banished {card} from {pile}', { card: cardLogLabel(removed), pile: msg(sourcePile.name) });
  }
  host.updatePileMeshes();
  host.setState((state) => ({
    searchPile: sourcePile.cards.length === 0 ? null : state.searchPile,
    contextMenu: null,
  }));
}
